import { SPACING_1, useToasts } from '@mnd-frontend/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { useEnvironmentVariables } from '../../../contexts/EnvironmentVariablesContext';
import { getCloudinaryImageUrl } from '../../../lib/getCloudinaryImageUrl';
import { fileReader } from '../../../lib/promisifiedFileReader';
import ErrorMessage from '../ErrorMessage';
import FileInputField from '../FileInputField';
import UploadWrapper from '../UploadWrapper';
import { FILE_ERROR_MESSAGE_TIMEOUT, ImageAspectRatio, UPLOADING_STATES } from '../constants';
import { type ValidatorProps, createImageValidator } from '../validators/img';
import ImageUploadPreview from './ImageUploadPreview';

type StyleProps = {
  $portrait?: boolean;
  $fullWidth: boolean;
};

export const Wrapper = styled(UploadWrapper)<StyleProps>`
  & img {
    margin: auto;
    ${props =>
      props.$fullWidth
        ? css`
            width: 100%;
          `
        : css`
            max-width: 100%;
            width: auto;
          `}
  }

  ${({ $portrait }) =>
    $portrait &&
    css`
      aspect-ratio: 1 / 1;

      img {
        margin: ${SPACING_1} auto;
        width: 120px;
        height: 120px;
        border-radius: 50%;
      }
    `};
`;

export const normalizeFile = async (file: File, setError: (e: string) => void) => {
  if (file.type !== 'image/svg+xml') return file;
  try {
    const svgString = (await fileReader(file, 'readAsText')) as string;
    // Must add xml declaration on the svg otherwise we can't upload it to Cloudinary
    const svgData = svgString.includes('<?xml')
      ? svgString
      : `<?xml version="1.0" encoding="utf-8"?>${svgString}`;
    const blob = new Blob([svgData], { type: 'image/svg+xml' });
    const newFile = new File([blob], file.name || 'logo.svg', {
      type: 'image/svg+xml',
    });
    return newFile;
  } catch (e: unknown) {
    setError((e as Error).message);
    return;
  }
};

type Props = {
  cloudinaryId?: string | null;
  imageUrl?: string;
  caption?: string;
  portrait?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  skipState?: boolean;
  loading?: boolean;
  localPreview?: boolean;
  action: (img: File) => void;
  deleteCallback?: () => void;
  editCropping?: () => void;
  validatorProps: ValidatorProps;
  fullWidth?: boolean;
};

const ImageUploader = ({
  cloudinaryId,
  imageUrl,
  caption,
  portrait,
  disabled,
  multiple,
  skipState,
  loading,
  localPreview = true,
  action,
  deleteCallback,
  editCropping,
  validatorProps,
  fullWidth = false,
}: Props) => {
  const {
    mndAssets: { cdnDistribution },
  } = useEnvironmentVariables();
  const [status, setStatus] = useState(UPLOADING_STATES.IDLE);
  const [image, setImage] = useState<File | null>(null);
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const { showFailureToast } = useToasts();

  const validator = createImageValidator(validatorProps);

  const handleFileError = ({ code }: { code: string }) => {
    switch (code) {
      case 'error.dimensions':
        return setError(t('components_image-upload_error_dimensions'));
      case 'error.unsafe':
        return setError('The provided svg contains harmful or unsafe content.');
      case 'error.size':
        return setError('The provided svg is too large.');
      case 'error.file':
        return setError('Invalid file');
      case 'error.fileformat':
      default:
        return setError(t('components_image-upload_error_format'));
    }
  };

  const handleFileDelete = () => {
    setImage(null);
    if (deleteCallback) deleteCallback();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    setError('');
    setStatus(UPLOADING_STATES.LOADING);
    for (const file of files) {
      if (validator) {
        try {
          await validator(file);
        } catch (e: unknown) {
          handleFileError(e as { code: string });
          setStatus(UPLOADING_STATES.IDLE);
          return;
        }
      }

      const normalizedFile = await normalizeFile(file, setError);
      if (!normalizedFile) return;
      await action(normalizedFile);
      if (!multiple && !skipState) setImage(file);
    }
    setStatus(UPLOADING_STATES.IDLE);
  };

  useEffect(() => {
    if (error) {
      showFailureToast(error);
    }
  }, [error, showFailureToast]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (timeout) {
        setError('');
      }
    }, FILE_ERROR_MESSAGE_TIMEOUT);
    return () => {
      clearTimeout(timeout);
    };
  }, [error]);

  return (
    <Wrapper $portrait={portrait} $fullWidth={fullWidth}>
      {error ? (
        <ErrorMessage caption={error} />
      ) : cloudinaryId || imageUrl || image ? (
        <ImageUploadPreview
          {...(cloudinaryId
            ? {
                cloudinaryId,
                imageUrl:
                  getCloudinaryImageUrl(cdnDistribution, cloudinaryId, null, 'LOGOTYPE') || '',
              }
            : {
                imageUrl: localPreview
                  ? imageUrl || (image && URL.createObjectURL(image)) || ''
                  : imageUrl || '',
              })}
          handleFileDelete={handleFileDelete}
          handleEditCropping={editCropping}
          aspectRatio={portrait ? ImageAspectRatio.Portrait_1_1 : ImageAspectRatio.Standard_4_3}
        />
      ) : (
        <FileInputField
          status={status}
          loading={loading}
          caption={caption || t('components_image-upload_new-image')}
          disabled={disabled}
          multiple={multiple}
          acceptFormats={validatorProps.validFileFormats}
          onChange={handleFileSelect}
        />
      )}
    </Wrapper>
  );
};

export default ImageUploader;
